import type { PriceQueryExpressListVO } from '@/api/sass/rate-query/express'
import { PARTNER_RATE_SOURCE } from '@/api/sass/rate-share/partner-types'

/** 比较可空重量边界，忽略接口中数值和数值字符串的小数位差异。 */
const sameWeight = (left?: number | string | null, right?: number | string | null): boolean => {
  const leftMissing = left === null || left === undefined
  const rightMissing = right === null || right === undefined
  if (leftMissing || rightMissing) return leftMissing && rightMissing
  return Number(left) === Number(right)
}

/**
 * 构造快递查询销售视图，仅映射服务端销售价和销售计算结果，不在前端重复计价。
 * 保留入参成本对象，用于运价维护、特价申请原价和应付费用等成本业务。
 *
 * @param record 快递查询接口返回的原始成本及商务价格
 * @returns 用于列表、详情和询价匹配展示的独立销售视图
 */
export const withExpressBusinessPrices = <T extends PriceQueryExpressListVO>(record: T): T => {
  // 伙伴价已经在接收方保存为最终价，没有来源商务规则对象；仍需展示本次销售试算总额。
  if (record.rateSource === PARTNER_RATE_SOURCE && record.salesCalculation) {
    return { ...record, calculation: record.salesCalculation }
  }
  const business = record.salesPrice ?? record.businessPrice
  // 同时具备销售单价与销售计算结果才切换视图，避免前后端分批发布时单价和总价混用。
  if (!business || !record.salesCalculation) return record

  return {
    ...record,
    tripPrice: business.unitPrice?.tripPrice?.sellPrice ?? record.tripPrice,
    kmPrice: business.unitPrice?.kmPrice?.sellPrice ?? record.kmPrice,
    onePriceAmount: business.unitPrice?.onePriceAmount?.sellPrice ?? record.onePriceAmount,
    firstAdditional: record.firstAdditional ? {
      ...record.firstAdditional,
      firstWeightPrice: business.firstAdditional?.firstWeightPrice?.sellPrice
        ?? record.firstAdditional.firstWeightPrice,
      additionalWeightPrice: business.firstAdditional?.additionalWeightPrice?.sellPrice
        ?? record.firstAdditional.additionalWeightPrice
    } : record.firstAdditional,
    unitPrice: record.unitPrice ? {
      ...record.unitPrice,
      unitPrice: business.unitPrice?.unitPrice?.sellPrice ?? record.unitPrice.unitPrice,
      tripPrice: business.unitPrice?.tripPrice?.sellPrice ?? record.unitPrice.tripPrice,
      kmPrice: business.unitPrice?.kmPrice?.sellPrice ?? record.unitPrice.kmPrice
    } : record.unitPrice,
    weightTiers: record.weightTiers?.map((tier, index) => {
      const prices = business.weightTiers?.[index]
      if (!prices
        || (prices.billingMethod ?? null) !== (tier.billingMethod ?? null)
        || (prices.sortOrder ?? null) !== (tier.sortOrder ?? null)
        || !sameWeight(prices.weightStart, tier.weightStart)
        || !sameWeight(prices.weightEnd, tier.weightEnd)) {
        return { ...tier }
      }
      return {
        ...tier,
        unitPrice: prices.unitPrice?.sellPrice ?? tier.unitPrice,
        additionalWeightPrice: prices.additionalWeightPrice?.sellPrice ?? tier.additionalWeightPrice
      }
    }),
    calculation: record.salesCalculation
  }
}
